import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function StaffLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome, ${res.user.name}`);
      nav("/portal", { replace: true });
    } else {
      setError(res.error || "Could not sign in.");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper" data-testid="staff-login">
      <div className="relative hidden lg:block bg-ink">
        <img
          src="https://images.unsplash.com/photo-1613977257363-707ba9348227?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85"
          alt=""
          className="w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="eyebrow text-white/60 mb-4">Staff Portal</div>
          <div className="font-heading font-light text-4xl leading-tight tracking-tight max-w-md">
            The studio, on the inside.
          </div>
          <div className="mt-4 text-white/70 text-sm max-w-md">
            Manage projects, folders, files and enquiries — from anywhere.
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 md:px-16 py-16">
        <div className="w-full max-w-md mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-stone hover:text-ink mb-12" data-testid="login-back-home">
            <ArrowLeft size={14} /> Back to website
          </Link>

          <div className="eyebrow mb-3">Staff access</div>
          <h1 className="font-heading font-light text-4xl lg:text-5xl tracking-tighter leading-tight mb-2">Sign in.</h1>
          <p className="text-stone text-sm">Authorised personnel only.</p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="login-form">
            <div>
              <label className="eyebrow block mb-1">Email</label>
              <input
                data-testid="login-email"
                type="email" required autoFocus
                className="line-input" value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label className="eyebrow block mb-1">Password</label>
              <input
                data-testid="login-password"
                type="password" required
                className="line-input" value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && (
              <div className="text-sm text-destructive font-mono" data-testid="login-error">{error}</div>
            )}

            <button
              type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-ink text-white py-4 text-sm hover:bg-ink/90 disabled:opacity-60 transition-colors"
            >
              {loading ? "Signing in…" : "Sign in →"}
            </button>

            <div className="flex items-center justify-between text-xs text-stone">
              <Link to="/staff/forgot-password" data-testid="login-forgot" className="hover:text-ink underline underline-offset-4">
                Forgot password?
              </Link>
              <span className="font-mono">AS · secure session</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
